import React from 'react'
import {
  AdCreative,
  AdCreativeType,
  AdImage,
  AdVideo,
  CreativeMediaType,
} from '@marketing-milk/interfaces'
import { formatCallToAction } from './formatCallToAction'

/**
 * The subset of `AdCreative` this presentational component actually reads. Kept
 * narrow so callers that don't have a full persisted creative (e.g. the creative
 * -import Review step, which only has in-flight edit state) can build a type-safe
 * object without casting. A full `AdCreative` stays assignable.
 */
export type LocalAdPreviewCreative = Pick<
  AdCreative,
  | 'format'
  | 'primaryText'
  | 'singleImage'
  | 'imageURL'
  | 'thumbnailURL'
  | 'linkDisplayURL'
  | 'linkHeadline'
  | 'linkDescription'
  | 'callToAction'
> & {
  /**
   * Narrowed to the three fields the video renderer reads, so a caller holding a
   * local blob URL (creative-import Review) can supply one without fabricating a
   * whole persisted `AdVideo`. A real `AdVideo` stays assignable.
   */
  singleVideo?: Pick<AdVideo, 'source' | 'storedSource' | 'thumbnail'>
  /**
   * Narrowed the same way as `singleVideo`, and for the same reason: the
   * creative-import Review holds blob URLs for slides that have no persisted
   * AdImage/AdVideo behind them yet. A real `AdCreativeCarouselSlide` stays
   * assignable.
   */
  carouselSlides?: Array<{
    linkHeadline?: string
    linkDescription?: string
    linkWebsiteURL?: string
    mediaType?: CreativeMediaType
    image?: Pick<AdImage, 'url'>
    video?: Pick<AdVideo, 'source' | 'storedSource' | 'thumbnail'>
  }>
}

/**
 * How the media is sized.
 *
 * - `cover` (default) — a square, centre-cropped box. What the FB feed shows, and
 *   what the live-iframe overlay in `MobilePreview` is sized against; do not change
 *   it for callers that stack this beneath the iframe.
 * - `native` — the asset's own aspect ratio, uncropped, bounded by `NATIVE_MAX_HEIGHT`.
 *   For surfaces whose job is to show what the asset actually is (creative import),
 *   where cropping a 9:16 story to a square hides most of the ad.
 */
export type LocalAdPreviewMediaFit = 'cover' | 'native'

export interface LocalAdPreviewProps {
  creative: LocalAdPreviewCreative
  /** Facebook page name shown in the header; falls back to a neutral label. */
  pageName?: string
  /** Facebook page avatar; falls back to an initials chip when absent. */
  pageImageURL?: string
  /** Media sizing. Defaults to `cover` — the square feed crop. */
  mediaFit?: LocalAdPreviewMediaFit
  /**
   * Render the page name / avatar / "Sponsored" header. Defaults to `true`.
   * Callers without a real page (import review) should pass `false` rather than
   * show a placeholder identity, which reads as broken.
   */
  showPageHeader?: boolean
}

/**
 * Formats whose local rendering is faithful enough to APPROVE against — this
 * gates client approval in the sprint-packet modal, not merely what draws.
 *
 * Deliberately still excludes carousel even though S7b gives it a real card row:
 * that row is an approximation good enough for an internal "did the right copy
 * land on the right card" check in creative import, not for a client signing off
 * without Facebook's own render. Widening this is a product decision about
 * approval, not a rendering one.
 */
export const isLocallyRenderable = (format: AdCreativeType) =>
  format === AdCreativeType.single_image || format === AdCreativeType.single_video

/** FB feed truncates the primary text and shows "… See more"; ~125 chars is the rule of thumb. */
const PRIMARY_TEXT_LIMIT = 125

/**
 * Ceiling for `native` media so a very tall asset can't push the card off-screen.
 * Deliberately a fixed pixel value, not viewport-relative: a height clamp on a
 * fixed-aspect image shrinks its *width* too, leaving the asset stranded in the
 * middle of a wider card. The caller controls apparent size via column width;
 * this only catches the extreme (9:16) case.
 */
const NATIVE_MAX_HEIGHT = 520

/** Keeps `native` placeholders from collapsing to nothing without imposing a ratio. */
const NATIVE_PLACEHOLDER_MIN_HEIGHT = 200

const colors = {
  card: '#ffffff',
  border: '#dadde1',
  ink: '#050505',
  muted: '#65676b',
  faint: '#8a8d91',
  linkStrip: '#f0f2f5',
  ctaBg: '#e4e6eb',
  ctaInk: '#050505',
  avatarBg: '#c4cdd6',
}

/**
 * Deliberately no frame, tint, or letterbox bands in `native` mode: a visible box
 * is what makes an unfilled aspect ratio read as broken. With no box, a tall asset
 * is simply tall — which is the honest rendering when mixed formats are intentional.
 */
function mediaStyle(fit: LocalAdPreviewMediaFit): React.CSSProperties {
  if (fit === 'native') {
    return {
      display: 'block',
      margin: '0 auto',
      width: 'auto',
      height: 'auto',
      maxWidth: '100%',
      maxHeight: NATIVE_MAX_HEIGHT,
    }
  }
  return { width: '100%', display: 'block', aspectRatio: '1 / 1', objectFit: 'cover' }
}

function placeholderStyle(fit: LocalAdPreviewMediaFit): React.CSSProperties {
  return {
    width: '100%',
    ...(fit === 'native' ? { minHeight: NATIVE_PLACEHOLDER_MIN_HEIGHT } : { aspectRatio: '1 / 1' }),
    background: colors.linkStrip,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: colors.faint,
    fontSize: 13,
  }
}

function initials(name: string): string {
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map(w => w[0]?.toUpperCase() ?? '')
    .join('')
}

function PageHeader({ pageName, pageImageURL }: { pageName: string; pageImageURL?: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 12px 8px' }}>
      {pageImageURL ? (
        <img
          src={pageImageURL}
          alt=""
          style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
        />
      ) : (
        <div
          aria-hidden
          style={{
            width: 40,
            height: 40,
            borderRadius: '50%',
            background: colors.avatarBg,
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 15,
            fontWeight: 600,
            flexShrink: 0,
          }}
        >
          {initials(pageName)}
        </div>
      )}
      <div style={{ minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: 14, color: colors.ink, lineHeight: 1.2 }}>
          {pageName}
        </div>
        <div style={{ fontSize: 12, color: colors.faint, lineHeight: 1.4 }}>Sponsored · 🌐</div>
      </div>
    </div>
  )
}

function PrimaryText({ text, padTop }: { text: string; padTop?: boolean }) {
  const [expanded, setExpanded] = React.useState(false)
  const truncatable = text.length > PRIMARY_TEXT_LIMIT
  const shown = truncatable && !expanded ? `${text.slice(0, PRIMARY_TEXT_LIMIT).trimEnd()}…` : text
  return (
    <div
      style={{
        // The page header normally supplies the top gap; pad here when it's hidden.
        padding: padTop ? '16px 14px 12px' : '0 12px 10px',
        fontSize: 14,
        lineHeight: 1.4,
        color: colors.ink,
        whiteSpace: 'pre-wrap',
      }}
    >
      {shown}
      {truncatable && (
        <>
          {' '}
          <button
            type="button"
            onClick={() => setExpanded(e => !e)}
            style={{
              border: 'none',
              background: 'transparent',
              padding: 0,
              font: 'inherit',
              color: colors.muted,
              cursor: 'pointer',
            }}
          >
            {expanded ? 'See less' : 'See more'}
          </button>
        </>
      )}
    </div>
  )
}

function ImageMedia({ url, fit }: { url?: string; fit: LocalAdPreviewMediaFit }) {
  if (!url) {
    return <div style={placeholderStyle(fit)}>No image</div>
  }
  return <img src={url} alt="" style={mediaStyle(fit)} />
}

function VideoUnavailable({ fit }: { fit: LocalAdPreviewMediaFit }) {
  return <div style={placeholderStyle(fit)}>Video unavailable</div>
}

/**
 * Native single-video playback for review. Prefers our durable stored copy
 * (`storedSource`) over the expiring Facebook `source`. If the video fails to
 * load, falls back to the poster image, then to a clear unavailable state — never
 * a broken <video> or <img>.
 */
function VideoMedia({
  source,
  poster,
  fit,
}: {
  source?: string
  poster?: string
  fit: LocalAdPreviewMediaFit
}) {
  const [errored, setErrored] = React.useState(false)

  if (source && !errored) {
    return (
      <video
        src={source}
        poster={poster}
        controls
        playsInline
        preload="metadata"
        onError={() => setErrored(true)}
        style={{ ...mediaStyle(fit), background: '#000' }}
      />
    )
  }
  if (poster) {
    return <ImageMedia url={poster} fit={fit} />
  }
  return <VideoUnavailable fit={fit} />
}

function LinkStrip({ creative }: { creative: LocalAdPreviewCreative }) {
  const { linkDisplayURL, linkHeadline, linkDescription, callToAction } = creative
  if (!linkDisplayURL && !linkHeadline && !linkDescription && !callToAction) return null
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '10px 12px',
        background: colors.linkStrip,
      }}
    >
      <div style={{ minWidth: 0, flex: 1 }}>
        {linkDisplayURL && (
          <div
            style={{
              fontSize: 12,
              letterSpacing: 0.3,
              textTransform: 'uppercase',
              color: colors.faint,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {linkDisplayURL}
          </div>
        )}
        {linkHeadline && (
          <div style={{ fontSize: 15, fontWeight: 600, color: colors.ink, lineHeight: 1.3 }}>
            {linkHeadline}
          </div>
        )}
        {linkDescription && (
          <div style={{ fontSize: 13, color: colors.muted, lineHeight: 1.3 }}>
            {linkDescription}
          </div>
        )}
      </div>
      {callToAction && (
        <button
          type="button"
          disabled
          style={{
            flexShrink: 0,
            border: 'none',
            borderRadius: 6,
            background: colors.ctaBg,
            color: colors.ctaInk,
            fontSize: 14,
            fontWeight: 600,
            padding: '8px 12px',
            cursor: 'default',
            whiteSpace: 'nowrap',
          }}
        >
          {formatCallToAction(callToAction)}
        </button>
      )}
    </div>
  )
}

/**
 * The carousel's horizontally scrolling card row. Each card carries its own
 * media, headline, and description — which is the whole point of reviewing a
 * carousel locally: the per-slide copy is the part you can't check anywhere else
 * before the ad exists on Facebook.
 *
 * Cards are a fixed width and the row scrolls, matching how the feed shows them
 * (you see the first card and part of the second). Media is square-cropped even
 * in `native` mode: unlike a single asset, a carousel's cards MUST agree on
 * shape or the row reads as broken, and Facebook crops them anyway.
 */
function CarouselMedia({
  slides,
  displayURL,
}: {
  slides: NonNullable<LocalAdPreviewCreative['carouselSlides']>
  displayURL?: string
}) {
  return (
    <div style={{ display: 'flex', gap: 8, overflowX: 'auto', padding: '0 12px 12px' }}>
      {slides.map((slide, index) => {
        const poster = slide.video?.thumbnail
        const source = slide.mediaType === 'video' ? poster : slide.image?.url
        return (
          <div
            key={index}
            style={{
              flex: '0 0 auto',
              width: 200,
              border: `1px solid ${colors.border}`,
              borderRadius: 6,
              overflow: 'hidden',
              background: colors.card,
            }}
          >
            {source ? (
              <img
                src={source}
                alt=""
                style={{
                  width: '100%',
                  aspectRatio: '1 / 1',
                  objectFit: 'cover',
                  display: 'block',
                }}
              />
            ) : (
              <div
                style={{
                  width: '100%',
                  aspectRatio: '1 / 1',
                  background: colors.linkStrip,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: colors.faint,
                  fontSize: 12,
                }}
              >
                {slide.mediaType === 'video' ? 'Video' : 'No media'}
              </div>
            )}
            <div style={{ background: colors.linkStrip, padding: '8px 10px' }}>
              {displayURL && (
                <div
                  style={{
                    color: colors.faint,
                    fontSize: 11,
                    textTransform: 'uppercase',
                    marginBottom: 2,
                  }}
                >
                  {displayURL}
                </div>
              )}
              <div style={{ color: colors.ink, fontSize: 13, fontWeight: 600, lineHeight: 1.3 }}>
                {slide.linkHeadline || <span style={{ color: colors.faint }}>No headline</span>}
              </div>
              {slide.linkDescription && (
                <div style={{ color: colors.muted, fontSize: 12, marginTop: 2 }}>
                  {slide.linkDescription}
                </div>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}

function Placeholder() {
  return (
    <div
      style={{
        padding: '28px 16px',
        textAlign: 'center',
        color: colors.muted,
        fontSize: 13,
        lineHeight: 1.5,
      }}
    >
      <div style={{ fontWeight: 600, color: colors.ink, marginBottom: 4 }}>Dynamic creative</div>
      Live Facebook preview unavailable — open in Facebook to review this format.
    </div>
  )
}

/**
 * A Facebook-independent, network-free approximation of the FB mobile-feed ad,
 * drawn entirely from fields already hydrated on the creative (no preview fetch).
 * Used as the instant base layer beneath the live FB iframe (see `MobilePreview`'s
 * `fallback`): it paints immediately and stays visible whenever the live FB
 * preview is slow, down, or token-expired, so an AE can still review and approve
 * the ad's copy, image, headline, display URL, and CTA.
 *
 * Faithful for `single_image` / `single_video`; `carousel` / `dynamic` render a
 * minimal informative placeholder (not a faked layout), and callers must not treat
 * the placeholder as an authoritative surface for approval.
 */
export function LocalAdPreview({
  creative,
  pageName,
  pageImageURL,
  mediaFit = 'cover',
  showPageHeader = true,
}: LocalAdPreviewProps) {
  const name = pageName?.trim() || 'Your Page'
  const isImage = creative.format === AdCreativeType.single_image
  const isVideo = creative.format === AdCreativeType.single_video
  const isCarousel = creative.format === AdCreativeType.carousel

  return (
    <div
      style={{
        width: '100%',
        background: colors.card,
        border: `1px solid ${colors.border}`,
        borderRadius: 8,
        overflow: 'hidden',
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
        textAlign: 'left',
      }}
    >
      {showPageHeader && <PageHeader pageName={name} pageImageURL={pageImageURL} />}
      {creative.primaryText && <PrimaryText text={creative.primaryText} padTop={!showPageHeader} />}
      {isImage && (
        <ImageMedia
          url={creative.singleImage?.url ?? creative.imageURL ?? creative.thumbnailURL}
          fit={mediaFit}
        />
      )}
      {isVideo && (
        <VideoMedia
          source={creative.singleVideo?.storedSource ?? creative.singleVideo?.source}
          poster={creative.singleVideo?.thumbnail ?? creative.thumbnailURL}
          fit={mediaFit}
        />
      )}
      {isCarousel && (
        <CarouselMedia
          slides={creative.carouselSlides ?? []}
          displayURL={creative.linkDisplayURL}
        />
      )}
      {(isImage || isVideo) && <LinkStrip creative={creative} />}
      {!isImage && !isVideo && !isCarousel && <Placeholder />}
    </div>
  )
}
